/**
 * アイコン（線画）。
 *
 * **デザインシステム v0.1 はアイコンを持たない**ため、mamemo-6 が規格を決めて自前で持つ
 * （ADR-033。規格は `design-system/README.md` §7 が正）。要点:
 *
 * - 24×24 の viewBox・線幅 1.5・端と角は丸・**塗りは使わない**（点だけ例外）
 * - 色は `currentColor`。**アイコン自身が色を持たない**ので、親の文字色に従う
 * - 既定サイズ 20px。密度の高いバー（アクセサリバー）では 22px
 *
 * 絵文字を使わない理由: 端末ごとに字形と彩度が変わり、色をこちらで決められない。
 * DESIGN-SYSTEM.md §10 の「多色化」「常時キラキラ」に触れる。
 *
 * 意味は `aria-label` / `title` を**呼び出し側が**付ける。ここは形だけを持つ。
 */

export type IconName =
  | 'back'
  | 'search'
  | 'trash'
  | 'settings'
  | 'history'
  | 'edit'
  | 'editor-code'
  | 'editor-plain'
  | 'outdent'
  | 'indent'
  | 'list'
  | 'heading'
  | 'quote'
  | 'bold'
  | 'italic'
  | 'code-inline'
  | 'code-block'
  | 'rule'
  | 'image'
  | 'extract'
  | 'range'
  | 'preview'
  | 'plus'
  | 'chevron-down'
  | 'chevron-right'
  | 'arrow-up'
  | 'arrow-down'
  | 'check';

/** 形の定義。stroke で描く。点など塗りが要るものだけ `dots` に持つ。 */
const SHAPES: Record<IconName, { d: string[]; dots?: Array<[number, number]> }> = {
  back: { d: ['M14.5 5.5 8 12l6.5 6.5'] },
  search: { d: ['M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13', 'm16 16 3.5 3.5'] },
  trash: {
    d: [
      'M4.5 7h15',
      'M9.5 7V5.5A1.5 1.5 0 0 1 11 4h2a1.5 1.5 0 0 1 1.5 1.5V7',
      'm6.5 7 .8 11.6A1.5 1.5 0 0 0 8.8 20h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7',
      'M10.5 11v5',
      'M13.5 11v5',
    ],
  },
  settings: {
    d: ['M4 7h9', 'M17 7h3', 'M4 12h3', 'M11 12h9', 'M4 17h9', 'M17 17h3'],
    dots: [
      [15, 7],
      [9, 12],
      [15, 17],
    ],
  },
  history: { d: ['M12 4a8 8 0 1 1 0 16 8 8 0 0 1 0-16', 'M12 7.5V12l3.2 2'] },
  edit: {
    d: [
      'm5 19 3.5-.8L19 6.7a1.5 1.5 0 0 0 0-2.1l-.6-.6a1.5 1.5 0 0 0-2.1 0L5.8 15.5z',
      'M15 5.7 17.4 8.1',
      'm5.8 15.5 2.7 2.7',
    ],
  },
  // CodeMirror（構文を意識する編集面）＝ 波かっこ
  'editor-code': {
    d: [
      'M10 4.5c-2 0-2 2.4-2 3.6s-.4 2.9-2.5 3.9c2.1 1 2.5 2.7 2.5 3.9s0 3.6 2 3.6',
      'M14 4.5c2 0 2 2.4 2 3.6s.4 2.9 2.5 3.9c-2.1 1-2.5 2.7-2.5 3.9s0 3.6-2 3.6',
    ],
  },
  // textarea（素の入力面）＝ 段落
  'editor-plain': { d: ['M5 7h14', 'M5 12h14', 'M5 17h9'] },
  outdent: { d: ['M9 7h11', 'M9 12h11', 'M9 17h11', 'm6.5 9.5-2.5 2.5 2.5 2.5'] },
  indent: { d: ['M9 7h11', 'M9 12h11', 'M9 17h11', 'm4 9.5 2.5 2.5L4 14.5'] },
  list: {
    d: ['M9.5 7h10.5', 'M9.5 12h10.5', 'M9.5 17h10.5'],
    dots: [
      [5, 7],
      [5, 12],
      [5, 17],
    ],
  },
  heading: { d: ['M6 5v14', 'M18 5v14', 'M6 12h12'] },
  // 引用 = 縦罫と本文（画面上の見え方と同じ形にする）
  quote: { d: ['M4.5 6v12', 'M9.5 8h10', 'M9.5 12h10', 'M9.5 16h6.5'] },
  bold: { d: ['M8 5v14', 'M8 5h4.6a3.5 3.5 0 0 1 0 7H8', 'M8 12h5.6a3.5 3.5 0 0 1 0 7H8'] },
  italic: { d: ['M10 5h6', 'M8 19h6', 'M14.5 5l-3 14'] },
  'code-inline': { d: ['m9.5 8.5-3.5 3.5 3.5 3.5', 'm14.5 8.5 3.5 3.5-3.5 3.5'] },
  'code-block': {
    d: ['M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2z', 'm10 10-2 2 2 2', 'm14 10 2 2-2 2'],
  },
  rule: { d: ['M4 12h16'] },
  image: {
    d: [
      'M4 6.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      'm4.5 16.5 4.5-4.5 3.5 3.5',
      'm13.5 14 2.5-2.5 3.5 3.5',
    ],
    dots: [[9, 9]],
  },
  // 切り出し = 枠の外へ持ち出す
  extract: { d: ['M14 4.5h5.5V10', 'M19.5 4.5 12 12', 'M17 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h4.5'] },
  // 期間指定 = 開始と終了をつなぐ
  range: { d: ['M6 12h12', 'm8.5 9.5-2.5 2.5 2.5 2.5', 'm15.5 9.5 2.5 2.5-2.5 2.5'] },
  preview: {
    d: [
      'M2.75 12S6.5 6.25 12 6.25 21.25 12 21.25 12 17.5 17.75 12 17.75 2.75 12 2.75 12',
      'M12 9.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5',
    ],
  },
  plus: { d: ['M12 5.5v13', 'M5.5 12h13'] },
  // 開閉の三角（▾ / ▸）の置き換え。文字グリフだと端末で字形が変わる。
  'chevron-down': { d: ['m6.5 9.5 5.5 5.5 5.5-5.5'] },
  'chevron-right': { d: ['m9.5 6.5 5.5 5.5-5.5 5.5'] },
  'arrow-up': { d: ['M12 19V5.5', 'm6.5 11 5.5-5.5 5.5 5.5'] },
  'arrow-down': { d: ['M12 5v13.5', 'm6.5 13 5.5 5.5 5.5-5.5'] },
  // 完了（保存済み）。DESIGN-SYSTEM.md §2 Small delight が名指しする「完了時の反応」の置き場。
  check: { d: ['m5 12.5 4.5 4.5L19 7.5'] },
};

export interface IconProps {
  name: IconName;
  /** px。既定 20（密度の高いバーでは 22 を渡す）。 */
  size?: number;
}

/**
 * 装飾として描く。意味は必ず**呼び出し側**が `aria-label` / `title` で与えること
 * （このコンポーネントは支援技術から隠れる）。
 */
export function Icon({ name, size = 20 }: IconProps) {
  const shape = SHAPES[name];
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {shape.d.map((d) => (
        <path key={d} d={d} />
      ))}
      {shape.dots?.map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.6} fill="currentColor" stroke="none" />
      ))}
    </svg>
  );
}
